/**
 * What the resume scan read off the CV — the key facts an HR reader wants beside the score: contact, last
 * employer, total experience, education, skills and cities. One component for the candidate review page and the
 * Recruitment Network panel, so the two can never disagree about what was read. A field the resume does not
 * state is simply absent; nothing here is guessed.
 */
import type { ReactNode } from "react";

export interface ResumeProfileData {
  name?: string;
  email?: string;
  phone?: string;
  city?: string;
  lastCompany?: string;
  lastTitle?: string;
  lastWorkingYm?: string;
  currentlyWorking?: boolean;
  education?: { degree: string; institution?: string; year?: string }[];
  /** Whole working history in years, computed from the dated roles. */
  totalYears?: number;
  skills?: string[];
  citiesWorked?: string[];
}

const monthLabel = (ym: string): string => {
  const [y, m] = ym.split("-");
  const d = new Date(Number(y), Number(m) - 1, 1);
  return Number.isNaN(d.getTime()) ? ym : d.toLocaleDateString(undefined, { month: "short", year: "numeric" });
};

const yearsLabel = (y: number): string => `${y % 1 === 0 ? y : y.toFixed(1)} year${y === 1 ? "" : "s"}`;

/** True when the profile has anything worth showing. */
export function hasResumeProfile(p: ResumeProfileData | null | undefined): p is ResumeProfileData {
  if (!p) return false;
  return Boolean(
    p.phone || p.email || p.city || p.lastCompany || p.lastTitle || p.currentlyWorking || p.lastWorkingYm ||
      p.totalYears || p.education?.length || p.skills?.length || p.citiesWorked?.length,
  );
}

/** Which profile keys feed each displayed row — used to mark a row a person edited. */
const ROW_KEYS: Record<string, readonly string[]> = {
  Experience: ["totalYears"],
  "Last company": ["lastCompany", "lastTitle"],
  "Last working date": ["lastWorkingYm", "currentlyWorking"],
  Education: ["education"],
  Phone: ["phone"],
  Email: ["email"],
  City: ["citiesWorked"],
  Cities: ["citiesWorked"],
};

export function ResumeProfileLines({
  profile,
  showSkills = true,
  edited,
}: {
  profile: ResumeProfileData;
  showSkills?: boolean;
  /** Keys a recruiter typed in (resume_overrides) — those rows say so. */
  edited?: readonly string[];
}): ReactNode {
  const rows: [string, ReactNode][] = [];
  if (profile.totalYears) rows.push(["Experience", `${yearsLabel(profile.totalYears)} in total`]);
  if (profile.lastCompany || profile.lastTitle) {
    rows.push(["Last company", [profile.lastCompany, profile.lastTitle].filter(Boolean).join(" · ")]);
  }
  if (profile.currentlyWorking) rows.push(["Last working date", "Currently working"]);
  else if (profile.lastWorkingYm) rows.push(["Last working date", monthLabel(profile.lastWorkingYm)]);
  if (profile.education?.length) {
    rows.push([
      "Education",
      <>
        {profile.education.map((e, i) => (
          <span key={i} className="t1-rv-profile-line">
            {[e.degree, e.institution, e.year].filter(Boolean).join(" · ")}
          </span>
        ))}
      </>,
    ]);
  }
  if (profile.phone) rows.push(["Phone", profile.phone]);
  if (profile.email) rows.push(["Email", profile.email]);
  const places = profile.citiesWorked?.length ? profile.citiesWorked : profile.city ? [profile.city] : [];
  if (places.length) rows.push([places.length > 1 ? "Cities" : "City", places.join(" · ")]);
  const skills = showSkills ? (profile.skills ?? []) : [];
  if (rows.length === 0 && skills.length === 0) return null;
  return (
    <>
      <dl className="t1-rv-profile">
        {rows.map(([k, v]) => (
          <div key={k}>
            <dt>{k}</dt>
            <dd>
              {v}
              {edited && ROW_KEYS[k]?.some((x) => edited.includes(x)) && <span className="t1-rv-edited">edited</span>}
            </dd>
          </div>
        ))}
      </dl>
      {skills.length > 0 && (
        <div className="t1-rv-skills" aria-label="Skills on the resume">
          {skills.map((s) => (
            <span key={s} className="pill t">
              {s}
            </span>
          ))}
        </div>
      )}
    </>
  );
}
